import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Field } from '../../../auth/field';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import FileUploadBox from '../../../create-curriculum/file-upload-box';
import { useQuery } from '@tanstack/react-query';
import { fetchCourseCategories } from '@eliadmin/core/src/api/courses/coursers-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { RiDeleteBin7Line } from 'react-icons/ri';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { TextareaCkeditor } from '@eliadmin/core/src/components/ui/textarea-ckeditor';
import { getCourseCategoryOptions, type CourseCategoryOptionSource } from '../course-form-values';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { normalizeCourseExternalUrlInput } from '../../course-external-link';

export const AddCourseStep0 = ({
  edit,
  selectedCategories = [],
}: {
  edit?: boolean;
  selectedCategories?: CourseCategoryOptionSource[];
}) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const form = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'instructors',
  });

  const isEdit = edit || false;

  const { data: courseCategories } = useQuery({
    queryKey: ['courseCategories', locale, 'form-tree'],
    queryFn: () => fetchCourseCategories({ locale, query: { childs: true } }),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-6 bg-white p-6 md:gap-8 md:rounded-lg md:p-8">
        <h1 className="text-2xl font-bold md:text-4xl">Údaje o kurze</h1>

        <Field name="name" label="Názov vzdelávacieho kurzu" required control={form.control} />

        <FileUploadBox
          name="image"
          texts={{
            placeholder: 'Nahrajte obrázok vzdelávacieho kurzu',
          }}
        />

        <Field
          name="categories"
          label="Kategória vzdelávacieho kurzu"
          type="select"
          options={getCourseCategoryOptions(courseCategories?.data, selectedCategories)}
          placeholder="Vyberte kategóriu"
          required
          control={form.control}
        />

        <Field
          name="courseType"
          label="Typ vzdelávacieho kurzu"
          type="checkbox-group"
          options={[
            { value: 'Doplnkový kurz', label: 'Doplnkový kurz' },
            { value: 'Akreditovaný kurz', label: 'Akreditovaný kurz' },
            { value: 'Rekvalifikačný kurz', label: 'Rekvalifikačný kurz' },
          ]}
          className={{
            wrapper: 'flex flex-col gap-2 md:flex-col',
          }}
          required
          control={form.control}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem required className="flex w-full min-w-0 flex-col gap-3">
              <FormLabel className="text-sm font-normal md:text-base">
                Popis vzdelávacieho kurzu
              </FormLabel>
              <FormControl>
                <TextareaCkeditor
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Stručný popis..."
                  minHeight={180}
                  maxHeight={420}
                  className="w-full min-w-0"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="content"
          control={form.control}
          render={({ field }) => (
            <FormItem required className="flex w-full min-w-0 flex-col gap-3">
              <FormLabel className="text-sm font-normal md:text-base">
                Obsah vzdelávacieho kurzu
              </FormLabel>
              <FormControl>
                <TextareaCkeditor
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Podrobný obsah..."
                  minHeight={180}
                  maxHeight={420}
                  className="w-full min-w-0"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Field
          name="targetAudience"
          label="Pre koho je určený vzdelávací kurz"
          type="textarea"
          placeholder="Cieľová skupina..."
          required
          control={form.control}
        />

        <Field
          name="confirmationForm"
          label="Forma potvrdenia po absolvovaní vzdelávacieho kurzu"
          type="checkbox-group"
          options={[
            { value: 'Certifikát', label: 'Certifikát' },
            { value: 'Osvedčenie', label: 'Osvedčenie' },
            {
              value: 'Osvedčenie o akreditovanom kurze',
              label: 'Osvedčenie o akreditovanom kurze',
            },
            { value: 'Licencia', label: 'Licencia' },
          ]}
          className={{
            wrapper: 'flex flex-col gap-2 md:flex-col',
          }}
          required
          control={form.control}
        />

        <Field
          name="learningMethod"
          label="Spôsob vzdelávania"
          type="checkbox-group"
          options={[
            { value: 'Prezenčné', label: 'Prezenčné' },
            { value: 'Online', label: 'Online' },
            { value: 'E-learning', label: 'E-learning' },
          ]}
          required
          className={{
            wrapper: 'flex flex-col gap-2 md:flex-col',
          }}
          control={form.control}
        />

        <Separator />

        <h2 className="text-xl font-semibold">Lektorovia</h2>
        {fields.map((field, index) => (
          <div key={field.id} className="flex gap-4">
            <div className="w-full">
              <Field
                name={`instructors.${index}.name`}
                label="Meno lektora"
                placeholder="Meno a priezvisko"
                required
                control={form.control}
              />
            </div>
            {fields.length > 1 && (
              <Button
                type="button"
                variant={'destructive'}
                className={cn(
                  'col-span-full mb-1 h-12 min-w-12 self-end p-0 text-sm font-medium text-destructive hover:underline',
                )}
                onClick={() => remove(index)}
              >
                <RiDeleteBin7Line className="h-5 w-5 text-destructive-foreground" />
              </Button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="text-sm font-medium text-primary hover:underline"
          onClick={() => append({ name: '' })}
        >
          Pridať lektora
        </button>

        <Separator />

        <Field
          name="courseLocation"
          label="Miesto kurzu"
          placeholder="Zadajte miesto konania kurzu"
          required
          control={form.control}
        />

        <div className="grid grid-cols-2 gap-4">
          <Field
            name="startDate"
            placeholder="Začiatok vzdelávacieho kurzu"
            type="date"
            control={form.control}
            required
          />
          <Field
            name="endDate"
            placeholder="Koniec vzdelávacieho kurzu"
            type="date"
            control={form.control}
            required
          />
          {form.formState.errors['date_start'] && (
            <div className="col-span-2 text-sm text-destructive">
              Začiatok vzdelávacieho kurzu musí byť pred ukončením vzdelávacieho kurzu.
            </div>
          )}
        </div>

        <Separator />

        <Field
          name="price"
          label="Cena s DPH"
          placeholder="Zadajte cenu v EUR, napr. 9,90"
          required
          control={form.control}
        />

        <FormField
          name="externalUrl"
          control={form.control}
          render={({ field, fieldState }) => (
            <FormItem className="space-y-1 p-[1px]">
              <FormLabel className="pb-2 pl-3 font-bold">
                Externý odkaz na kurz / e-book (nepovinné)
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="www.vasadomena.sk/kurz"
                  value={field.value ?? ''}
                  onBlur={() => {
                    const normalized = normalizeCourseExternalUrlInput(field.value);
                    if (fieldState.isDirty && normalized && normalized !== field.value) {
                      field.onChange(normalized);
                    }
                    field.onBlur();
                  }}
                  className={{ main: 'rounded-full px-5', wrapper: 'rounded-full' }}
                />
              </FormControl>
              <div className="pl-3">
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-3">
          <Field
            name="canBeReimbursed"
            label="Možnosť preplatiť úradom práce"
            type="checkbox"
            control={form.control}
          />
          <Field
            name="requiresExam"
            label="Potrebná skúška odbornej spôsobilosti pri podnikaní alebo pri zamestnaní"
            type="checkbox"
            control={form.control}
          />
        </div>
      </div>
    </div>
  );
};
